// src/Utils/serverSelector.js
import axios from "axios";
import { headers } from "../redux/apiConfig";
import { getJwtToken } from "./authStorage";

const RAW_PROD = process.env.REACT_APP_SERVER_URL_MEDIUM || "";
const RAW_DEV  = process.env.REACT_APP_SERVER_URL_TEST  || "";

const PROD_URL = RAW_PROD.trim().replace(/\/+$/, "");
const DEV_URL  = RAW_DEV.trim().replace(/\/+$/, "");

const envIsDevFlag = String(process.env.REACT_APP_IS_DEV || "").trim().toLowerCase();
export const IS_DEV = envIsDevFlag
  ? envIsDevFlag === "true"
  : process.env.NODE_ENV === "development";

// IS_DEV=true  → PRIMARY: TEST,   SECONDARY: MEDIUM
// IS_DEV=false → PRIMARY: MEDIUM, SECONDARY: TEST
const PRIMARY_URL   = IS_DEV ? DEV_URL  : PROD_URL;
const SECONDARY_URL = IS_DEV ? PROD_URL : DEV_URL;

const PING_RETRIES     = 4;   // сколько раз пробуем PRIMARY перед переключением
const PING_RETRY_DELAY = 800; // мс между попытками

let selectedServerUrl = PRIMARY_URL || SECONDARY_URL || "";

// ===============================
//   Utils
// ===============================
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

const getServerList = () => {
  return [PRIMARY_URL, SECONDARY_URL].filter(Boolean);
};

const getFallbackServer = (currentUrl) => {
  return getServerList().find((url) => url && url !== currentUrl) || null;
};

const isServerUnavailableError = (error) => {
  if (!error) return false;

  // сеть, timeout, DNS, CORS, server down
  if (!error.response) return true;

  // 5xx = серверная ошибка, можно пробовать fallback
  const status = error.response.status;
  return status >= 500;
};

const pingServer = async (baseUrl) => {
  if (!baseUrl) {
    throw new Error("URL сервера не задан");
  }

  return axios.get(`${baseUrl}/ping`, {
    headers,
    timeout: 5000,
    withCredentials: true,
  });
};

/**
 * Пингует сервер до `retries` раз с задержкой между попытками.
 * Возвращает true если хотя бы одна попытка удалась, иначе false.
 */
const pingWithRetries = async (
  baseUrl,
  retries = PING_RETRIES,
  delay = PING_RETRY_DELAY
) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      await pingServer(baseUrl);
      // console.log(
      //   `[serverSelector] ✅ Сервер доступен (попытка ${attempt}/${retries}): ${baseUrl}`
      // );
      return true;
    } catch (err) {
      console.warn(
        `[serverSelector] ❌ Попытка ${attempt}/${retries} к ${baseUrl}: ${err?.message || err}`
      );
      if (attempt < retries) await sleep(delay);
    }
  }
  return false;
};

// ===============================
//   Server URL
// ===============================
export const getServerUrl = ({ strict = false } = {}) => {
  if (selectedServerUrl) return selectedServerUrl;

  const firstAvailable = PRIMARY_URL || SECONDARY_URL || "";
  if (firstAvailable) {
    selectedServerUrl = firstAvailable;
    return selectedServerUrl;
  }

  if (strict) {
    throw new Error("[serverSelector] Не задан ни один SERVER_URL в .env");
  }

  console.warn("[serverSelector] Нет URL сервера");
  return "";
};

/**
 * Проверяет доступность серверов и выбирает рабочий.
 * Сначала пробует PRIMARY (MEDIUM) несколько раз,
 * только потом переключается на SECONDARY (TEST).
 */
export const initServerUrl = async () => {
  const servers = getServerList();

  if (!servers.length) {
    throw new Error("[serverSelector] Не задан ни один SERVER_URL в .env");
  }

  for (let i = 0; i < servers.length; i++) {
    const serverUrl = servers[i];
    const isPrimary = i === 0;
    const retries   = isPrimary ? PING_RETRIES : 1;

    const ok = await pingWithRetries(serverUrl, retries);

    if (ok) {
      selectedServerUrl = serverUrl;
      // console.log(`[serverSelector] 🟢 Выбран сервер: ${serverUrl}`);
      return selectedServerUrl;
    }

    if (isPrimary) {
      console.warn(
        `[serverSelector] PRIMARY (${serverUrl}) недоступен после ${retries} попыток. Переключаемся на резервный...`
      );
    }
  }

  throw new Error("[serverSelector] Нет доступных серверов");
};

export const resetServerUrl = () => {
  selectedServerUrl = PRIMARY_URL || SECONDARY_URL || "";
};

export const switchToFallbackServer = async () => {
  const currentUrl  = getServerUrl({ strict: true });
  const fallbackUrl = getFallbackServer(currentUrl);

  if (!fallbackUrl) {
    throw new Error("[serverSelector] Резервный сервер не найден");
  }

  const ok = await pingWithRetries(fallbackUrl, 1);
  if (!ok) {
    throw new Error(`[serverSelector] Резервный сервер недоступен: ${fallbackUrl}`);
  }

  selectedServerUrl = fallbackUrl;
  return selectedServerUrl;
};

// можно вызвать позже, не блокируя UI
export const warmupServer = () => {
  const serverUrl = getServerUrl();
  if (!serverUrl) return;

  axios
    .get(`${serverUrl}/ping`, {
      headers,
      timeout: 5000,
      withCredentials: true,
    })
    .catch(() => {});
};

// ===============================
//   Headers с JWT
// ===============================
export const getAuthHeaders = async (extraHeaders = {}) => {
  let token = null;

  try {
    token = await getJwtToken();
  } catch (e) {
    console.warn("⚠️ getJwtToken error:", e?.message || e);
  }

  const baseHeaders = {
    ...(headers || {}),
    ...extraHeaders,
  };

  if (token) {
    return {
      ...baseHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  return baseHeaders;
};

// ===============================
//   Axios instance
// ===============================
export const createAuthAxios = async () => {
  const baseURL     = getServerUrl({ strict: true });
  const authHeaders = await getAuthHeaders();

  const instance = axios.create({
    baseURL,
    headers: authHeaders,
    timeout: 10000,
    withCredentials: true,
  });

  return instance;
};

// ===============================
//   Request with fallback
// ===============================
export const apiRequest = async ({
  url,
  method = "GET",
  headers: extraHeaders = {},
  data = null,
  timeout = 10000,
  params = undefined,
}) => {
  if (!url) {
    throw new Error("apiRequest: url is required");
  }

  let baseUrl = getServerUrl();

  if (!baseUrl) {
    baseUrl = await initServerUrl();
  }

  const jwtToken = await getJwtToken();

  const finalHeaders = {
    "Content-Type": "application/json",
    ...(headers || {}),
    ...(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}),
    ...extraHeaders,
  };

  try {
    const response = await axios({
      url: `${baseUrl}${url}`,
      method,
      headers: finalHeaders,
      data,
      params,
      timeout,
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    // если это не проблема доступности сервера — fallback не нужен
    if (!isServerUnavailableError(error)) {
      throw new Error(error?.response?.data?.message || error.message);
    }

    const fallbackUrl = getFallbackServer(baseUrl);

    if (!fallbackUrl) {
      throw new Error(error?.response?.data?.message || error.message);
    }

    try {
      await pingServer(fallbackUrl);

      const retryResponse = await axios({
        url: `${fallbackUrl}${url}`,
        method,
        headers: finalHeaders,
        data,
        params,
        timeout,
        withCredentials: true,
      });

      selectedServerUrl = fallbackUrl;

      return retryResponse.data;
    } catch (fallbackError) {
      throw new Error(
        fallbackError?.response?.data?.message ||
          fallbackError.message ||
          "Оба сервера недоступны"
      );
    }
  }
};

// ===============================
//   Helpers
// ===============================
export const apiGet = (url, config = {}) =>
  apiRequest({ url, method: "GET", ...config });

export const apiPost = (url, data = null, config = {}) =>
  apiRequest({ url, method: "POST", data, ...config });

export const apiPut = (url, data = null, config = {}) =>
  apiRequest({ url, method: "PUT", data, ...config });

export const apiPatch = (url, data = null, config = {}) =>
  apiRequest({ url, method: "PATCH", data, ...config });

export const apiDelete = (url, config = {}) =>
  apiRequest({ url, method: "DELETE", ...config });